import i18next from "i18next";

import { SignupAttribution } from "@/types/auth";

import { ATTRIBUTION_CODE_TTL_MS } from "./data";

const ATTRIBUTION_STORAGE_KEY = "signup_attribution";
const PROMO_CODE_STORAGE_KEY = "promo_code";

export const PROMO_CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

// What the backend accepts as `language` at sign-up.
const SIGNUP_LANGUAGES = ["en", "es", "es-AR", "it", "pt"] as const;
type SignupLanguage = (typeof SIGNUP_LANGUAGES)[number];

const UTM_PARAMS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
} as const;

// Only marketing params are kept from the landing URL: others can carry tokens (reset, session, Telegram).
const LANDING_PARAMS = new Set([
  ...Object.values(UTM_PARAMS),
  "ref",
  "affcode",
  "clickid",
  "sub1",
  "sub2",
  "sub3",
  "sub4",
  "sub5",
  "promo",
  "lang",
  "gclid",
  "fbclid",
  "ttclid",
]);

interface StoredAttribution extends SignupAttribution {
  expiresAt: number;
}

interface StoredPromoCode {
  code: string;
  capturedAt: number;
}

const readJson = <T>(key: string): T | undefined => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

const writeJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to store ${key}`, error);
  }
};

const remove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to clear ${key}`, error);
  }
};

const hasUtm = (value: Partial<SignupAttribution>) =>
  Object.keys(UTM_PARAMS).some((key) => Boolean(value[key as keyof typeof UTM_PARAMS]));

const landingPathOf = (location: Location): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(location.search).forEach((value, key) => {
    if (LANDING_PARAMS.has(key.toLowerCase())) kept.append(key, value.slice(0, 200));
  });
  const query = kept.toString();
  return `${location.pathname}${query ? `?${query}` : ""}`.slice(0, 500);
};

// External referrers only, without their query string; a reload of our own page is not a source.
const externalReferrer = (): string | undefined => {
  try {
    if (!document.referrer) return undefined;
    const url = new URL(document.referrer);
    if (url.host === window.location.host) return undefined;
    return `${url.origin}${url.pathname}`.slice(0, 500);
  } catch {
    return undefined;
  }
};

const readLiveAttribution = (): StoredAttribution | undefined => {
  const stored = readJson<StoredAttribution>(ATTRIBUTION_STORAGE_KEY);
  if (!stored?.expiresAt) return undefined;
  if (Date.now() > stored.expiresAt) {
    remove(ATTRIBUTION_STORAGE_KEY);
    return undefined;
  }
  return stored;
};

/**
 * First touch for 7 days: a live capture is kept, except that a visit with UTM
 * tags replaces one that had none (a direct visit before the ad click).
 */
export const captureAttributionFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const next: Partial<SignupAttribution> = {};
    for (const [field, param] of Object.entries(UTM_PARAMS)) {
      const value = params.get(param)?.trim().slice(0, 200);
      if (value) next[field as keyof typeof UTM_PARAMS] = value;
    }

    const live = readLiveAttribution();
    if (live && (hasUtm(live) || !hasUtm(next))) return;

    const now = Date.now();
    const value: StoredAttribution = {
      ...next,
      landingPath: landingPathOf(window.location),
      referrer: externalReferrer(),
      capturedAt: new Date(now).toISOString(),
      expiresAt: now + ATTRIBUTION_CODE_TTL_MS,
    };
    writeJson(ATTRIBUTION_STORAGE_KEY, value);
  } catch (error) {
    console.warn("Failed to capture attribution", error);
  }
};

export const getStoredAttribution = (): SignupAttribution | undefined => {
  const live = readLiveAttribution();
  if (!live) return undefined;
  return {
    utmSource: live.utmSource,
    utmMedium: live.utmMedium,
    utmCampaign: live.utmCampaign,
    utmContent: live.utmContent,
    utmTerm: live.utmTerm,
    landingPath: live.landingPath,
    referrer: live.referrer,
    capturedAt: live.capturedAt,
  };
};

export const clearStoredAttribution = () => remove(ATTRIBUTION_STORAGE_KEY);

/** i18n's language as one the backend stores: "es-ar" → "es-AR", "pt-BR" → "pt". */
export const signupLanguage = (language = i18next.language): SignupLanguage | undefined => {
  const clean = language?.trim().toLowerCase();
  if (!clean) return undefined;
  const exact = SIGNUP_LANGUAGES.find((code) => code.toLowerCase() === clean);
  if (exact) return exact;
  const base = clean.split(/[-_]/)[0];
  return SIGNUP_LANGUAGES.find((code) => code === base);
};

/** Added to every sign-up payload (form, Google, Telegram, TON). */
export const signupSourcePayload = () => ({
  attribution: getStoredAttribution(),
  language: signupLanguage(),
});

export const normalizePromoCode = (code?: string | null): string =>
  (code ?? "").trim().toUpperCase().replace(/\s+/g, "");

/** Keeps a ?promo= code (or one typed at sign-up) for 7 days; the newest one wins. */
export const storePromoCode = (raw?: string | null) => {
  const code = normalizePromoCode(raw);
  if (!PROMO_CODE_PATTERN.test(code)) return;
  writeJson(PROMO_CODE_STORAGE_KEY, { code, capturedAt: Date.now() } satisfies StoredPromoCode);
};

export const capturePromoCodeFromUrl = () =>
  storePromoCode(new URLSearchParams(window.location.search).get("promo"));

export const getStoredPromoCode = (): string | undefined => {
  const stored = readJson<StoredPromoCode>(PROMO_CODE_STORAGE_KEY);
  if (!stored?.code || !stored.capturedAt) return undefined;
  if (Date.now() - stored.capturedAt > ATTRIBUTION_CODE_TTL_MS) {
    remove(PROMO_CODE_STORAGE_KEY);
    return undefined;
  }
  return stored.code;
};

export const clearStoredPromoCode = () => remove(PROMO_CODE_STORAGE_KEY);
